/**
 * Non-modal popover anchored to its trigger button (account menu, display settings).
 * Keyboard: the trigger toggles it, Escape closes it and returns focus to the trigger, Tab moves
 * through its controls; clicking outside closes it. The panel is rendered in a portal on <body> and
 * positioned `fixed` from the trigger's rectangle, clamped to the viewport: a scrolling or sticky
 * container (the Files sidebar is both — and a sticky box is its own stacking context, so the main
 * navigation painted over an in-place panel) can neither clip it nor cover it.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** Where the panel goes: under the trigger (above when there is no room), clamped to the viewport. */
export function popoverPosition(btn: { left: number; right: number; top: number; bottom: number }, panel: { width: number; height: number }, vw: number, vh: number, align: 'start' | 'end', gap = 8, margin = 8): { left: number; top: number } {
  const wanted = align === 'end' ? btn.right - panel.width : btn.left;
  const left = Math.max(margin, Math.min(wanted, vw - panel.width - margin));
  const below = btn.bottom + gap;
  const top = below + panel.height > vh - margin && btn.top - gap - panel.height >= margin ? btn.top - gap - panel.height : Math.max(margin, Math.min(below, vh - panel.height - margin));
  return { left, top };
}

export function Popover({
  label,
  trigger,
  triggerClassName = 'xc-icon-btn',
  align = 'end',
  tabIndex,
  children,
}: {
  /** Accessible name of the panel (and the trigger's tooltip). */
  label: string;
  trigger: ReactNode;
  triggerClassName?: string;
  align?: 'start' | 'end';
  /** For triggers inside a roving-tabindex widget (a tree row): only the active row's buttons are tab stops. */
  tabIndex?: number;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<CSSProperties | undefined>(undefined);

  const close = (focusTrigger = true) => {
    setOpen(false);
    if (focusTrigger) button.current?.focus();
  };

  useLayoutEffect(() => {
    if (!open) { setPos(undefined); return; }
    const place = () => {
      const b = button.current?.getBoundingClientRect();
      const el = panel.current;
      if (!b || !el) return;
      const { left, top } = popoverPosition(b, { width: el.offsetWidth, height: el.offsetHeight }, window.innerWidth, window.innerHeight, align);
      setPos({ position: 'fixed', left, top, right: 'auto', visibility: 'visible' });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!root.current?.contains(t) && !panel.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Move focus into the panel so screen readers announce it — once it has its place: until then the
  // panel is `visibility: hidden`, and a hidden element cannot take focus.
  const placed = open && pos !== undefined;
  useEffect(() => {
    if (!placed) return;
    const first = [...(panel.current?.querySelectorAll<HTMLElement>('button, [href], input, select, [tabindex]') ?? [])]
      .find((el) => el.tabIndex >= 0 && !el.matches(':disabled') && el.getClientRects().length > 0);
    (first ?? panel.current)?.focus();
  }, [placed]);

  return (
    <div className="xc-popover-root" ref={root}>
      <button
        ref={button}
        type="button"
        className={triggerClassName}
        aria-expanded={open}
        aria-controls={id}
        aria-haspopup="dialog"
        aria-label={label}
        title={label}
        tabIndex={tabIndex}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger}
      </button>
      {open && createPortal(
        <div ref={panel} id={id} role="dialog" aria-label={label} tabIndex={-1} className={`xc-popover xc-popover-${align}`} style={pos ?? { position: 'fixed', left: 0, top: 0, right: 'auto', visibility: 'hidden' }}
          onBlur={(e) => {
            const to = e.relatedTarget as Node | null;
            if (to && !root.current?.contains(to) && !panel.current?.contains(to)) setOpen(false);
          }}>
          {children(() => close())}
        </div>,
        document.body,
      )}
    </div>
  );
}
